import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import * as E from '@react-email/components'
import {
  json,
  redirect,
  type ActionFunctionArgs,
  type MetaFunction,
} from '@remix-run/node'
import { Form, useActionData, useSearchParams } from '@remix-run/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import {
  ProviderConnectionForm,
  providerNames,
} from '#app/utils/connections.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { sendEmail } from '#app/utils/email.server'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.js'
import { EmailSchema } from '#app/utils/user-validation.ts'
import { prepareVerification } from './verify.server.ts'

const SignupSchema = z.object({
  email: EmailSchema,
})

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData()

  checkHoneypot(formData)

  const submission = await parseWithZod(formData, {
    schema: SignupSchema.superRefine(async (data, ctx) => {
      const existingUser = await prisma.user.findUnique({
        where: { email: data.email },
        select: { id: true },
      })
      if (existingUser) {
        ctx.addIssue({
          path: ['email'],
          code: z.ZodIssueCode.custom,
          message: 'Esiste già un utente con questa email',
        })
        return
      }
    }),
    async: true,
  })
  if (submission.status !== 'success') {
    return json(
      { result: submission.reply() },
      { status: submission.status === 'error' ? 400 : 200 },
    )
  }
  const { email } = submission.value
  const { verifyUrl, redirectTo, otp } = await prepareVerification({
    period: 10 * 60,
    request,
    type: 'onboarding',
    target: email,
  })

  const response = await sendEmail({
    to: email,
    subject: `Benvenuto in Tobilì!`,
    react: <SignupEmail onboardingUrl={verifyUrl.toString()} otp={otp} />,
  })

  if (response.status === 'success') {
    return redirect(redirectTo.toString())
  } else {
    return json(
      {
        result: submission.reply({ formErrors: [response.error.message] }),
      },
      {
        status: 500,
      },
    )
  }
}

export function SignupEmail({
  onboardingUrl,
  otp,
}: {
  onboardingUrl: string
  otp: string
}) {
  return (
    <E.Html lang="it" dir="ltr">
      <E.Container>
        <h1>
          <E.Text>Benvenuto in Tobili!</E.Text>
        </h1>
        <p>
          <E.Text>
            Ecco il tuo codice di verifica: <strong>{otp}</strong>
          </E.Text>
        </p>
        <p>
          <E.Text>Oppure clicca il link per verificare:</E.Text>
        </p>
        <E.Link href={onboardingUrl}>{onboardingUrl}</E.Link>
      </E.Container>
    </E.Html>
  )
}

export const meta: MetaFunction = () => {
  return [{ title: 'Registrati · Tobilì' }]
}

export default function SignupRoute() {
  const actionData = useActionData<typeof action>()
  const isPending = useIsPending()
  const [searchParams] = useSearchParams()
  const redirectTo = searchParams.get('redirectTo')

  const [form, fields] = useForm({
    id: 'signup-form',
    constraint: getZodConstraint(SignupSchema),
    lastResult: actionData?.result,
    onValidate({ formData }) {
      const result = parseWithZod(formData, { schema: SignupSchema })
      return result
    },
    shouldRevalidate: 'onBlur',
  })

  return (
    <div className="container flex flex-col justify-center pb-32 pt-20">
      <div className="text-center">
        <h1 className="text-h1">Inizia il tuo percorso!</h1>
        <p className="mt-3 text-body-md text-muted-foreground">
          Inserisci la tua email.
        </p>
      </div>
      <div className="mx-auto mt-16 min-w-full max-w-sm sm:min-w-[368px]">
        <Form method="POST" {...getFormProps(form)}>
          <HoneypotInputs />
          <Field
            labelProps={{
              htmlFor: fields.email.id,
              children: 'Email',
            }}
            inputProps={{
              ...getInputProps(fields.email, { type: 'email' }),
              autoFocus: true,
              autoComplete: 'email',
            }}
            errors={fields.email.errors}
          />
          <ErrorList errors={form.errors} id={form.errorId} />
          <StatusButton
            className="w-full"
            status={isPending ? 'pending' : form.status ?? 'idle'}
            type="submit"
            disabled={isPending}
          >
            Invia
          </StatusButton>
        </Form>
        <ul className="mt-5 flex flex-col gap-5 border-b-2 border-t-2 border-border py-3">
          {providerNames.map(providerName => (
            <li key={providerName}>
              <ProviderConnectionForm
                type="Signup"
                providerName={providerName}
                redirectTo={redirectTo}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />
}
